import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { getGoogleMapsApiKey } from '@/services/googleMapsService';

interface LocationThumbnailProps {
  targetUrl: string;
  targetCode: string;
}

// Cache the API key to avoid repeated edge function calls
let cachedApiKey: string | null = null;
let apiKeyPromise: Promise<string | null> | null = null;

const getCachedMapsApiKey = async (): Promise<string | null> => {
  if (cachedApiKey) return cachedApiKey;
  
  if (!apiKeyPromise) {
    apiKeyPromise = getGoogleMapsApiKey().then(key => {
      cachedApiKey = key;
      return key;
    });
  }
  
  return apiKeyPromise;
};

// Extract coordinates from various URL formats
const extractCoordinates = (targetUrl: string): { lat: string; lng: string } | null => {
  try {
    // Check if it's a direct coordinate string
    const coordMatch = targetUrl.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
    if (coordMatch) {
      return { lat: coordMatch[1], lng: coordMatch[2] };
    }
    // If the URL is a Google Maps URL, try to extract coordinates
    const mapsMatch = targetUrl.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (mapsMatch) {
      return { lat: mapsMatch[1], lng: mapsMatch[2] };
    }
    return null;
  } catch {
    return null;
  }
};

export const LocationThumbnail: React.FC<LocationThumbnailProps> = ({ targetUrl, targetCode }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadThumbnail = async () => {
      const coords = extractCoordinates(targetUrl);
      if (!coords) {
        setError(true);
        setLoading(false);
        return;
      }

      const apiKey = await getCachedMapsApiKey();
      if (!apiKey) {
        setError(true);
        setLoading(false);
        return;
      }

      const url = `https://maps.googleapis.com/maps/api/streetview?size=200x150&location=${coords.lat},${coords.lng}&key=${apiKey}`;
      setThumbnailUrl(url);
      setLoading(false);
    };

    loadThumbnail();
  }, [targetUrl]);

  if (loading) {
    return (
      <div className="w-14 h-14 rounded-md bg-muted animate-pulse flex items-center justify-center">
        <MapPin className="h-6 w-6 text-muted-foreground/50" />
      </div>
    );
  }

  if (error || !thumbnailUrl) {
    return (
      <div className="w-14 h-14 rounded-md bg-muted flex items-center justify-center">
        <span className="text-2xl">📍</span>
      </div>
    );
  }

  return (
    <div className="w-14 h-14 rounded-md overflow-hidden bg-muted flex items-center justify-center">
      <img
        src={thumbnailUrl}
        alt={`Location target ${targetCode}`}
        className="w-full h-full object-cover"
        onError={() => setError(true)}
      />
    </div>
  );
};

export default LocationThumbnail;
